import { useCallback, useEffect, useRef, useState } from 'react';
import { GameEngine, type GameSnapshot } from './engine';
import { Renderer } from './renderer';
import type { BallArt } from './art';
import { MAX_ASPECT, MIN_ASPECT, WORLD_WIDTH } from './constants';

interface UseGameOptions {
  ballCount: number;
  art: BallArt;
  onGameOver?: (score: number) => void;
}

/**
 * Owns the game loop and wires pointer input to the engine.
 *
 * The engine and renderer are deliberately plain classes living in refs: they change
 * 60 times a second, and putting that in React state would re-render the tree on every
 * frame. React only hears about the coarse snapshot (score, phase) a few times a
 * second, via `snapshot`.
 */
export function useGame({ ballCount, art, onGameOver }: UseGameOptions) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const engineRef = useRef<GameEngine | null>(null);
  const rendererRef = useRef<Renderer | null>(null);
  const artRef = useRef(art);
  const overRef = useRef(onGameOver);
  const [snapshot, setSnapshot] = useState<GameSnapshot | null>(null);
  const [generation, setGeneration] = useState(0);

  artRef.current = art;
  overRef.current = onGameOver;

  const restart = useCallback(() => setGeneration((g) => g + 1), []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;

    // Board height follows the space available, clamped so it stays the same game.
    const box = wrap.getBoundingClientRect();
    const aspect = Math.min(MAX_ASPECT, Math.max(MIN_ASPECT, box.height / box.width));
    const worldHeight = Math.round(WORLD_WIDTH * aspect);

    const engine = new GameEngine({
      ballCount,
      worldHeight,
      onGameOver: (score) => overRef.current?.(score),
    });
    const renderer = new Renderer(canvas, engine, artRef.current);
    engineRef.current = engine;
    rendererRef.current = renderer;

    const fit = () => {
      const rect = wrap.getBoundingClientRect();
      // Letterbox to the board's aspect so the world is never distorted.
      const scale = Math.min(rect.width / WORLD_WIDTH, rect.height / worldHeight);
      renderer.resize(WORLD_WIDTH * scale, worldHeight * scale);
      renderer.readTheme();
    };
    fit();

    const observer = new ResizeObserver(fit);
    observer.observe(wrap);

    // The canvas paints its own board colours, so it has to be told when the system
    // switches between light and dark.
    const scheme = window.matchMedia('(prefers-color-scheme: dark)');
    const onScheme = () => renderer.readTheme();
    scheme.addEventListener('change', onScheme);

    let raf = 0;
    let last = performance.now();
    let sinceSnapshot = 0;

    const frame = (now: number) => {
      // Clamp the delta so returning to a backgrounded tab doesn't teleport the world.
      const elapsed = Math.min(now - last, 100);
      last = now;

      engine.update(elapsed);
      renderer.draw();

      sinceSnapshot += elapsed;
      if (sinceSnapshot > 80) {
        sinceSnapshot = 0;
        setSnapshot(engine.snapshot);
      }

      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    setSnapshot(engine.snapshot);

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
      scheme.removeEventListener('change', onScheme);
      engine.destroy();
      engineRef.current = null;
      rendererRef.current = null;
    };
  }, [ballCount, generation]);

  useEffect(() => {
    rendererRef.current?.setArt(art);
  }, [art]);

  // --- Pointer input ---

  const toWorldX = useCallback((clientX: number): number => {
    const canvas = canvasRef.current;
    if (!canvas) return 0;
    const rect = canvas.getBoundingClientRect();
    return ((clientX - rect.left) / rect.width) * WORLD_WIDTH;
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.currentTarget.setPointerCapture(e.pointerId);
      engineRef.current?.aim(toWorldX(e.clientX));
    },
    [toWorldX],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      // Aim on hover for mouse, but for touch only while the finger is down.
      if (e.pointerType !== 'mouse' && e.buttons === 0) return;
      engineRef.current?.aim(toWorldX(e.clientX));
    },
    [toWorldX],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      const engine = engineRef.current;
      if (!engine) return;
      engine.aim(toWorldX(e.clientX));
      engine.drop();
    },
    [toWorldX],
  );

  return {
    canvasRef,
    wrapRef,
    snapshot,
    restart,
    handlers: { onPointerDown, onPointerMove, onPointerUp },
  };
}
